import type { MouseEvent } from 'react'
import type { JSX } from 'react'

import { SecondaryOpacityFavoriteAButton } from 'atoms/ActionButton/Templates/SecondaryOpacityFavoriteButton'
import {
  DetailsSecondaryOpacityButton,
  PlayPrimaryButton,
  SecondaryOpacityTrailerButton,
} from 'atoms/Button/Templates'
import { PaymentButton } from 'atoms/Button/Templates/PaymentButton'
import { IconNames } from 'atoms/Icon'
import { Link } from 'atoms/Link'
import { TrailerPlayer } from 'atoms/TrailerPlayer'
import {
  Banner,
  BannerAlignItems,
  BannerCursor,
  BannerDescription,
  BannerDescriptionRowGap,
  BannerJustifyContent,
  BannerOverlayDirection,
} from 'blocks/Banner'
import { ButtonGroup } from 'blocks/ButtonGroup'
import { TextBlockBannerHeadlineExtra10 } from 'blocks/TextBlock/Templates/Banners/TextBlockBannerHeadlineExtra10'
import {
  BANNER_ASPECT_RATIO,
  BANNER_DESCRIPTION_WIDTH,
  BANNER_PADDINGS,
  BANNER_WIDTH,
  COLOR,
} from 'vars/index'
import { ButtonsClickGuard } from './ContentBannerCardTemplate.styled'
import { BANNER_BUTTON_HEIGHT } from './lib'

export const BANNER_TRAILER_BTN_TEST_ID = 'banner-trailer-btn'

export interface IContentBannerCardTemplateProps {
  width?: BANNER_WIDTH
  aspectRatio?: BANNER_ASPECT_RATIO
  justifyContent?: BannerJustifyContent
  alignItems?: BannerAlignItems
  paddings?: BANNER_PADDINGS
  overlayDirection?: BannerOverlayDirection
  overlayWidth?: number
  isCoverFallbackImage?: boolean
  overlayEnabled?: boolean
  descriptionWidth?: BANNER_DESCRIPTION_WIDTH
  rowGap?: BannerDescriptionRowGap

  setRef?: (node: HTMLDivElement | null) => void
  title?: string
  description?: string
  /** Channel logo shown above the title on channel banners */
  logoUrl?: string
  labels?: string[]
  contentLabels?: string[]
  quality?: string
  dolbyAtmos?: boolean
  verticalVideo?: boolean
  locked?: boolean
  paid?: boolean
  rented?: boolean
  purchaseTitle?: string
  isFree?: boolean
  posterUrl?: string
  posterMobileUrl?: string
  horizontalCovers?: Record<string, string>
  verticalCovers?: Record<string, string>
  playTitle?: string
  onPlayClick?: () => void
  showHowToWatchButton?: boolean | null
  howToWatchTitle?: string
  onHowToWatchClick?: () => void
  showSignInToWatchButton?: boolean | null
  signInToWatchTitle?: string
  onSignInClick?: () => void
  playerLink?: string
  trailerLink?: string
  trailerTitle?: string
  trailerDataTestId?: string
  detailsLink?: string
  detailsTitle?: string
  /** Called when the banner (outside its buttons) is clicked to open content details */
  onDetailsClick?: () => void
  /** Called when the banner (outside its buttons) is clicked to start playback, for content without a details page */
  onBannerPlayClick?: () => void
  isGuest?: boolean | null
  isFavorite?: boolean
  onFavoriteClick?: () => void
  lockedTitle?: string
  onLockedClick?: () => void

  /** Called when banner becomes visible in viewport */
  onBecameVisible?: () => void
  /** Is trailer player visible (hides poster when true) */
  isTrailerPlayerVisible?: boolean
  /** True for infinite-loop carousel clone instances of this banner */
  isClone?: boolean
  shouldRenderTrailerPlayer?: boolean
  showAclBlockedLabel?: boolean
  /** Age rating shown above the title when the banner is PIN-locked. */
  ageRatingLabel?: string
}

export const ContentBannerCardTemplate = ({
  width = BANNER_WIDTH.SIZE_100,
  aspectRatio = BANNER_ASPECT_RATIO.SIZE_10,
  justifyContent = BannerJustifyContent.Flex_end,
  alignItems = BannerAlignItems.Start,
  paddings = BANNER_PADDINGS.SIZE_40,
  overlayDirection = BannerOverlayDirection.OVERLAY_HORIZONTAL,
  overlayWidth = 100,
  isCoverFallbackImage = true,
  overlayEnabled = true,
  descriptionWidth = BANNER_DESCRIPTION_WIDTH.SIZE_10,
  rowGap = BannerDescriptionRowGap.INDENT_60,

  setRef,
  title = '',
  description = '',
  logoUrl = '',
  labels,
  contentLabels,
  quality = '',
  dolbyAtmos = false,
  verticalVideo = false,
  locked = false,
  paid = false,
  rented = false,
  purchaseTitle = '',
  isFree = false,
  posterUrl = '',
  posterMobileUrl = '',
  horizontalCovers = undefined,
  verticalCovers = undefined,
  playTitle = '',
  onPlayClick = () => {},
  showHowToWatchButton = false,
  howToWatchTitle = '',
  onHowToWatchClick = () => {},
  showSignInToWatchButton = false,
  signInToWatchTitle = '',
  onSignInClick = () => {},
  playerLink = '',
  trailerLink = '',
  trailerTitle = '',
  trailerDataTestId = BANNER_TRAILER_BTN_TEST_ID,
  detailsLink = '',
  detailsTitle = '',
  onDetailsClick = () => {},
  onBannerPlayClick,
  isGuest = true,
  isFavorite = false,
  onFavoriteClick = () => {},
  lockedTitle = '',
  onLockedClick = () => {},
  onBecameVisible,
  isTrailerPlayerVisible,
  isClone,
  shouldRenderTrailerPlayer,
  showAclBlockedLabel = false,
  ageRatingLabel,
}: IContentBannerCardTemplateProps): JSX.Element => {
  // Movie/TV Show banners have no visible Details button (FP-4333); clicking
  // the banner itself opens details instead. Channels have no details page, so
  // their banner click starts playback (FP-4357). Locked and geoblocked banners
  // keep their existing, unrelated interactions untouched.
  const onBannerClick = detailsLink ? onDetailsClick : onBannerPlayClick
  const isBannerClickable =
    !locked && !showAclBlockedLabel && Boolean(onBannerClick)

  const stopClickPropagation = (event: MouseEvent): void => {
    // Buttons inside the banner (Play, How To Watch, Sign In, Trailer,
    // Favorite) have their own actions and must not also trigger the
    // banner's details click.
    event.stopPropagation()
  }

  return (
    <Banner
      width={width}
      aspectRatio={aspectRatio}
      justifyContent={justifyContent}
      alignItems={alignItems}
      paddings={paddings}
      overlayDirection={overlayDirection}
      overlayWidth={overlayWidth}
      title={title}
      locked={locked}
      isCoverFallbackImage={isCoverFallbackImage}
      overlayEnabled={overlayEnabled}
      posterUrl={posterUrl}
      posterMobileUrl={posterMobileUrl}
      horizontalCovers={horizontalCovers}
      verticalCovers={verticalCovers}
      onBecameVisible={onBecameVisible}
      isTrailerPlayerVisible={isTrailerPlayerVisible}
      isClone={isClone}
      shouldRenderTrailerPlayer={shouldRenderTrailerPlayer}
      showAclBlockedLabel={showAclBlockedLabel}
      onClick={isBannerClickable ? onBannerClick : undefined}
      cursor={isBannerClickable ? BannerCursor.Pointer : undefined}
    >
      <BannerDescription width={descriptionWidth} rowGap={rowGap}>
        <TextBlockBannerHeadlineExtra10
          title={title}
          description={description}
          logoUrl={logoUrl}
          labels={labels}
          contentLabels={contentLabels}
          quality={quality}
          dolbyAtmos={dolbyAtmos}
          verticalVideo={verticalVideo}
          locked={locked}
          paid={paid}
          rented={rented}
          purchaseTitle={purchaseTitle}
          ageRatingLabel={ageRatingLabel}
        />
        {!locked && (
          <>
            {!showAclBlockedLabel && (
              <ButtonsClickGuard onClick={stopClickPropagation}>
                <ButtonGroup maxHeight={BANNER_BUTTON_HEIGHT.NORMAL}>
                  {isFree && (
                    <Link href={playerLink}>
                      {playTitle && (
                        <PlayPrimaryButton
                          title={playTitle}
                          onClick={onPlayClick}
                        />
                      )}
                    </Link>
                  )}

                  {showHowToWatchButton && (
                    <PaymentButton
                      title={howToWatchTitle}
                      onClick={onHowToWatchClick}
                    />
                  )}

                  {showSignInToWatchButton && (
                    <PaymentButton
                      title={signInToWatchTitle}
                      onClick={onSignInClick}
                    />
                  )}

                  {trailerLink && trailerTitle && (
                    <Link href={trailerLink}>
                      <SecondaryOpacityTrailerButton
                        dataTestId={trailerDataTestId}
                        title={trailerTitle}
                      />
                    </Link>
                  )}

                  {!isGuest && (
                    <SecondaryOpacityFavoriteAButton
                      selected={isFavorite}
                      onClick={onFavoriteClick}
                    />
                  )}
                </ButtonGroup>
              </ButtonsClickGuard>
            )}

            {showAclBlockedLabel && detailsLink && (
              <ButtonGroup maxHeight={BANNER_BUTTON_HEIGHT.NORMAL}>
                <Link href={detailsLink}>
                  <DetailsSecondaryOpacityButton
                    title={detailsTitle}
                    backgroundColorDefault={COLOR.BUTTON_ACCENT_PRIMARY}
                    backgroundColorHovered={COLOR.SURFACE_OPACITY_LIGHT_15}
                  />
                </Link>
              </ButtonGroup>
            )}
          </>
        )}

        {locked && (
          <ButtonGroup maxHeight={BANNER_BUTTON_HEIGHT.NORMAL}>
            <PlayPrimaryButton
              icon={IconNames.pinLock}
              title={lockedTitle}
              onClick={onLockedClick}
            />
          </ButtonGroup>
        )}
      </BannerDescription>
      <TrailerPlayer setRef={setRef} />
    </Banner>
  )
}
